import { useEffect, useMemo, useState } from "react";
import { UploadCloud, Loader2, X } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import {
  BusinessService,
  ServiceStaffMember,
} from "../../types/businessService";
import { Employee } from "../../types/employee";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../hooks/useToast";

type DiscountType = "percent" | "fixed";
type TimeUnit = "minutes" | "hours";

export interface ServiceFormInput {
  name: string;
  slug: string;
  price: number;
  discount: number;
  discountType: DiscountType;
  priceAfterDiscount: number;
  duration: number;
  timeUnit: TimeUnit;
  staff: ServiceStaffMember[];
  status: "active" | "inactive";
  categoryId: string;
  description: string;
  taxes: number[];
}

export interface Tax {
  id: number;
  name: string;
  percent: number;
  status: string;
}

interface ServiceImage {
  name: string;
  size: number;
  type: string;
  url: string;
}

interface ServiceModalProps {
  isOpen: boolean;
  mode: "add" | "edit";
  service?: BusinessService | null;
  onClose: () => void;
  onSave: (data: ServiceFormInput, images?: { newFiles: File[]; existingImages: string[]; defaultImage?: string }) => void;
  categories: { id: string; name: string }[];
  activeCategory: string;
  employees: Employee[];
  employeesLoading?: boolean;
  categoriesLoading?: boolean;
  taxes?: Tax[];
  taxesLoading?: boolean;
  existingImages?: ServiceImage[];
  saving?: boolean;
}

export const ServiceModal = ({
  isOpen,
  mode,
  service,
  onClose,
  onSave,
  categories = [],
  activeCategory,
  employees = [],
  employeesLoading = false,
  categoriesLoading = false,
  taxes = [],
  taxesLoading = false,
  existingImages = [],
  saving = false,
}: ServiceModalProps) => {
  const { user } = useAuth();
  const toast = useToast();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";

  // Ensure categories is always an array
  const safeCategories = Array.isArray(categories) ? categories : [];

  // Debug logging
  useEffect(() => {
    if (isOpen) {
      console.log("ServiceModal - Categories received:", categories);
      console.log("ServiceModal - Safe categories:", safeCategories);
      console.log("ServiceModal - Categories loading:", categoriesLoading);
    }
  }, [isOpen, categories, safeCategories, categoriesLoading]);

  const [formData, setFormData] = useState<ServiceFormInput>({
    name: "",
    slug: "",
    price: 0,
    discount: 0,
    discountType: "percent",
    priceAfterDiscount: 0,
    duration: 30,
    timeUnit: "minutes",
    staff: [],
    status: "active",
    categoryId: "",
    description: "",
    taxes: [],
  });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [currentImages, setCurrentImages] = useState<ServiceImage[]>([]);
  const [defaultImage, setDefaultImage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (service) {
        const initialTimeUnit =
          service.timeUnit ||
          (service.duration >= 60 && service.duration % 60 === 0
            ? "hours"
            : "minutes");
        const timeValue =
          initialTimeUnit === "hours"
            ? Math.max(1, Math.round((service.duration || 60) / 60))
            : service.duration || 30;
        setFormData({
          name: service.name,
          slug: service.slug || "",
          price: service.price || 0,
          discount: service.discount || 0,
          discountType:
            (service.discountType as DiscountType) || ("percent" as DiscountType),
          priceAfterDiscount: service.discountPrice || service.price || 0,
          duration: timeValue,
          timeUnit: initialTimeUnit as TimeUnit,
          staff: service.staff ?? [],
          status: service.status,
          categoryId: service.categoryId || activeCategory,
          description: service.description || "",
          taxes: (() => {
            const taxArray = service.taxes ? service.taxes.map(t => typeof t === 'string' ? parseInt(t) : t).filter(id => !isNaN(id) && id > 0) : [];
            console.log("ServiceModal - Converting taxes:", service.taxes, "to:", taxArray);
            return taxArray;
          })(),
        });
        setSlugManuallyEdited(Boolean(service.slug));
        // Images will be set from existingImages prop
        setCurrentImages(existingImages || []);
      } else {
        // For add mode, use activeCategory if available, otherwise use first category
        const defaultCatId = activeCategory || (safeCategories.length > 0 ? safeCategories[0].id : "");
        setFormData({
          name: "",
          slug: "",
          price: 0,
          discount: 0,
          discountType: "percent",
          priceAfterDiscount: 0,
          duration: 30,
          timeUnit: "minutes",
          staff: [],
          status: "active",
          categoryId: defaultCatId,
          description: "",
          taxes: [],
        });
        setSlugManuallyEdited(false);
        setUploadedFiles([]);
        setCurrentImages([]);
        setDefaultImage(null);
      }
    }
  }, [isOpen, service, activeCategory, categories, existingImages]);

  useEffect(() => {
    if (!slugManuallyEdited) {
      const generated = formData.name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      setFormData((prev) => ({
        ...prev,
        slug: generated,
      }));
    }
  }, [formData.name, slugManuallyEdited]);

  const priceAfterDiscount = useMemo(() => {
    const price = Number(formData.price) || 0;
    const discount = Number(formData.discount) || 0;
    if (formData.discountType === "percent") {
      return Math.max(price - price * (discount / 100), 0);
    }
    return Math.max(price - discount, 0);
  }, [formData.price, formData.discount, formData.discountType]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      formData.name.trim() &&
      formData.duration > 0 &&
      formData.price >= 0
    ) {
      const durationInMinutes =
        formData.timeUnit === "hours"
          ? formData.duration * 60
          : formData.duration;

      if (durationInMinutes > 180) {
        toast.error("Service duration cannot exceed 3 hours (180 minutes).");
        return;
      }

      if (durationInMinutes < 15) {
        toast.error("Service duration must be at least 15 minutes.");
        return;
      }

      // Discount validation
      if (formData.discount > 0) {
        if (formData.discountType === "percent" && formData.discount > 100) {
          toast.error("Discount percentage cannot exceed 100%");
          return;
        }
        if (formData.discountType === "fixed" && formData.discount > formData.price) {
          toast.error("Discount amount cannot exceed the service price");
          return;
        }
      }

      // Generate slug from name if empty
      const slug = formData.slug.trim() || formData.name.trim().toLowerCase().replace(/\s+/g, "-");

      // Prepare image data
      const existingImageNames = currentImages.map(img => img.name);
      const imageData = {
        newFiles: uploadedFiles,
        existingImages: existingImageNames,
        defaultImage: defaultImage || undefined,
      };

      onSave({
        name: formData.name.trim(),
        slug: slug,
        price: formData.price,
        discount: formData.discount,
        discountType: formData.discountType,
        priceAfterDiscount,
        duration: durationInMinutes,
        timeUnit: formData.timeUnit,
        staff: formData.staff,
        status: formData.status,
        categoryId: formData.categoryId,
        description: formData.description,
        taxes: formData.taxes,
      }, imageData);
    }
  };

  const handleStaffToggle = (employee: Employee) => {
    const employeeId = String(employee.id);
    setFormData((prev) => {
      const exists = prev.staff.some((member) => member.id === employeeId);
      const updatedStaff = exists
        ? prev.staff.filter((member) => member.id !== employeeId)
        : [
          ...prev.staff,
          {
            id: employeeId,
            name: employee.name,
          },
        ];
      return { ...prev, staff: updatedStaff };
    });
  };

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    setUploadedFiles(prev => [...prev, ...files]);
  };

  const handleRemoveUploadedFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (imageName: string) => {
    setCurrentImages(prev => prev.filter(img => img.name !== imageName));
    if (defaultImage === imageName) {
      setDefaultImage(null);
    }
  };

  const handleSetDefaultImage = (imageName: string) => {
    setDefaultImage(imageName);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/40 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-lg w-full max-w-4xl p-0 flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === "add" ? "Add New Service" : "Edit Service"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto px-6 py-6 space-y-6"
        >
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Service Name<span className="text-red-500">*</span>
              </label>
              <input
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g. Hair Cut"
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Service Slug<span className="text-red-500">*</span>
              </label>
              <input
                value={formData.slug}
                onChange={(e) => {
                  setSlugManuallyEdited(true);
                  setFormData((prev) => ({ ...prev, slug: e.target.value }));
                }}
                placeholder="hair-cut"
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Price ({currencySymbol})
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.price || ""}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    price: val === "" ? 0 : parseFloat(val),
                  }));
                }}
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-gray-700">
                  Discount
                </label>
                <div className="inline-flex rounded-lg border border-gray-300">
                  {(["percent", "fixed"] as DiscountType[]).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, discountType: type }))
                      }
                      className={`px-3 py-1 text-xs font-medium ${formData.discountType === type
                        ? "bg-[#885ABB] text-white"
                        : "text-gray-600"
                        }`}
                    >
                      {type === "percent" ? "Percent" : "Fixed"}
                    </button>
                  ))}
                </div>
              </div>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.discount || ""}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    discount: val === "" ? 0 : parseFloat(val),
                  }));
                }}
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Price after discount ({currencySymbol})
              </label>
              <input
                value={priceAfterDiscount.toFixed(2)}
                readOnly
                className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-gray-600"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Category
              </label>
              <Select
                value={formData.categoryId}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, categoryId: value }))
                }
                disabled={categoriesLoading}
              >
                <SelectTrigger className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-[#885ABB] focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  <SelectValue placeholder={categoriesLoading ? "Loading categories..." : "Select category"} />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200 shadow-lg rounded-lg">
                  {categoriesLoading ? (
                    <div className="px-2 py-1.5 text-sm text-gray-500">
                      Loading categories...
                    </div>
                  ) : safeCategories.length > 0 ? (
                    safeCategories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-2 py-1.5 text-sm text-gray-500">
                      No categories available
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Time required
              </label>
              <input
                type="number"
                min="1"
                value={formData.duration || ""}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    duration: val === "" ? 0 : parseInt(val),
                  }));
                }}
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Time unit
              </label>
              <Select
                value={formData.timeUnit}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    timeUnit: value as TimeUnit,
                  }))
                }
              >
                <SelectTrigger className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-[#885ABB] focus:ring-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200 shadow-lg rounded-lg">
                  <SelectItem value="minutes">Minutes</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Select Tax
              </label>
              {taxesLoading ? (
                <div className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-500">
                  Loading taxes...
                </div>
              ) : taxes.length > 0 ? (
                <div className="mt-1 space-y-2">
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-1">
                    {taxes.map((tax) => {
                      const isSelected = formData.taxes.includes(tax.id);
                      return (
                        <label
                          key={tax.id}
                          className={`flex items-center space-x-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${isSelected
                            ? "border-[#885ABB] bg-[#f6f2ff]"
                            : "border-gray-200 hover:bg-gray-50"
                            }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              setFormData((prev) => ({
                                ...prev,
                                taxes: isSelected
                                  ? prev.taxes.filter(id => id !== tax.id)
                                  : [...prev.taxes, tax.id],
                              }));
                            }}
                            className="rounded border-gray-300 text-[#885ABB] focus:ring-[#885ABB]"
                          />
                          <span className="flex-1">{tax.name} ({tax.percent}%)</span>
                        </label>
                      );
                    })}
                  </div>
                  {formData.taxes.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.taxes.map((taxId) => {
                        const tax = taxes.find(t => t.id === taxId);
                        return tax ? (
                          <span
                            key={taxId}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-[#f6f2ff] text-[#885ABB] rounded-md text-xs"
                          >
                            {tax.name} ({tax.percent}%)
                            <button
                              type="button"
                              onClick={() => {
                                setFormData((prev) => ({
                                  ...prev,
                                  taxes: prev.taxes.filter(id => id !== taxId),
                                }));
                              }}
                              className="hover:text-[#6f47a2]"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-500">
                  No taxes available
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Status
              </label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    status: value as "active" | "inactive",
                  }))
                }
              >
                <SelectTrigger className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-[#885ABB] focus:ring-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200 shadow-lg rounded-lg">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700">
                Assign Employee
              </label>
              <span className="text-xs text-gray-400">
                {formData.staff.length} selected
              </span>
            </div>
            {employeesLoading ? (
              <p className="mt-3 text-sm text-gray-500">Loading employees...</p>
            ) : employees.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">
                No team members available. Add employees first.
              </p>
            ) : (
              <div className="mt-2 grid grid-cols-2 gap-2">
                {employees.map((employee) => {
                  const employeeId = String(employee.id);
                  const checked = formData.staff.some(
                    (member) => member.id === employeeId
                  );
                  return (
                    <label
                      key={employeeId}
                      className={`flex items-center space-x-2 rounded-lg border px-3 py-2 text-sm ${checked
                        ? "border-[#885ABB] bg-[#f6f2ff]"
                        : "border-gray-200"
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleStaffToggle(employee)}
                        className="rounded border-gray-300 text-[#885ABB] focus:ring-[#885ABB]"
                      />
                      <span className="truncate">{employee.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-semibold text-gray-700">
              Service Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              rows={5}
              placeholder="Describe the service..."
              className="mt-2 w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-gray-700">
              Service Media
            </label>

            {/* Existing Images */}
            {currentImages.length > 0 && (
              <div className="mt-2 grid grid-cols-3 gap-3">
                {currentImages.map((image) => (
                  <div key={image.name} className="relative group">
                    <img
                      src={image.url}
                      alt={image.name}
                      className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveExistingImage(image.name)}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    {defaultImage === image.name && (
                      <div className="absolute bottom-1 left-1 px-2 py-1 bg-[#885ABB] text-white text-xs rounded">
                        Default
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => handleSetDefaultImage(image.name)}
                      className="absolute bottom-1 right-1 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      Set Default
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Area */}
            <label className="mt-2 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center text-sm text-gray-500 cursor-pointer hover:border-[#885ABB] hover:text-[#885ABB] transition-colors">
              <UploadCloud className="mb-2 h-8 w-8" />
              <p className="font-medium">Drop files here to upload</p>
              <p className="mt-1 text-xs text-gray-400">
                Recommended resolution 1920 × 1080
              </p>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelection}
                className="hidden"
              />
            </label>

            {/* New Uploaded Files Preview */}
            {uploadedFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-xs font-medium text-gray-600">New files to upload:</p>
                <div className="flex flex-wrap gap-2">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
                      <span className="text-sm text-gray-700 truncate max-w-[200px]">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveUploadedFile(index)}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <X className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!formData.name.trim() || saving}
              className="px-6 py-2 rounded-xl bg-[#885ABB] hover:bg-[#6f47a2] text-white transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>{mode === "add" ? "Add Service" : "Save Changes"}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
